import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import '../styles/ManageBatch.css';

const ManageBatch = () => {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const HOST = process.env.REACT_APP_HOST_NAME;

  const [batch, setBatch] = useState(null);
  const [enrolledUsers, setEnrolledUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [removing, setRemoving] = useState(null);

  // Form states
  const [enrollEmail, setEnrollEmail] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // Alert state
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });

  useEffect(() => {
    // Check if user is admin
    if (user?.role !== 'admin') {
      navigate('/');
      return;
    }
    
    fetchBatchDetails();
    fetchEnrolledUsers();
  }, [batchId, user]);

  const showAlert = (message, type = 'success') => {
    setAlert({ show: true, message, type });
    setTimeout(() => {
      setAlert({ show: false, message: '', type: '' });
    }, 3000);
  };

  const fetchBatchDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${HOST}api/get-premium-batch/${batchId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch batch details');
      }

      setBatch(data.batch || data);
    } catch (error) {
      showAlert(error.message || 'Error fetching batch details', 'error');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEnrolledUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await fetch(`${HOST}api/enrolled-users/${batchId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch enrolled users');
      }

      setEnrolledUsers(data.enrolledUsers || []);
    } catch (error) {
      showAlert(error.message || 'Error fetching enrolled users', 'error');
      console.error('Error:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleEnrollUser = async (e) => {
    e.preventDefault();
    
    if (!enrollEmail.trim()) {
      showAlert('Please enter an email address', 'error');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(enrollEmail)) {
      showAlert('Please enter a valid email address', 'error');
      return;
    }

    try {
      setEnrolling(true);
      const response = await fetch(`${HOST}api/enroll-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          batchId,
          email: enrollEmail.toLowerCase().trim()
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to enroll user');
      }

      showAlert(data.message || 'User enrolled successfully!', 'success');
      setEnrollEmail('');
      fetchEnrolledUsers();
      fetchBatchDetails(); // Refresh to update count
    } catch (error) {
      showAlert(error.message || 'Error enrolling user', 'error');
      console.error('Error:', error);
    } finally {
      setEnrolling(false);
    }
  };

  const handleRemoveUser = async (userId, userEmail) => {
    if (!window.confirm(`Are you sure you want to remove ${userEmail} from this batch?`)) {
      return;
    }

    try {
      setRemoving(userId);
      const response = await fetch(`${HOST}api/remove-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          batchId,
          userId
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to remove user');
      }

      showAlert(data.message || 'User removed successfully!', 'success');
      fetchEnrolledUsers();
      fetchBatchDetails(); // Refresh to update count
    } catch (error) {
      showAlert(error.message || 'Error removing user', 'error');
      console.error('Error:', error);
    } finally {
      setRemoving(null);
    }
  };

  // Filter users based on search and status
  const filteredUsers = enrolledUsers.filter(enrollment => {
    const matchesSearch = 
      enrollment.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      enrollment.userId?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || enrollment.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  // Statistics
  const stats = {
    total: enrolledUsers.length,
    active: enrolledUsers.filter(e => e.status === 'active').length,
    expired: enrolledUsers.filter(e => e.status === 'expired').length,
    suspended: enrolledUsers.filter(e => e.status === 'suspended').length
  };

  if (loading) {
    return (
      <div>
        <Navbar />
        <div className="loading-container">
          <div className="loader"></div>
          <p>Loading batch details...</p>
        </div>
      </div>
    );
  }

  if (!batch) {
    return (
      <div>
        <Navbar />
        <div className="error-container">
          <h2>Batch not found</h2>
          <button onClick={() => navigate('/premium')}>Back to Batches</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div className="manage-batch-container">
        {/* Alert */}
        {alert.show && (
          <div className={`alert alert-${alert.type}`}>
            {alert.message}
          </div>
        )}

        {/* Header */}
        <div className="manage-header">
          <div className="header-content">
            <button 
              className="back-button"
              onClick={() => navigate(`/premium-batch/${batchId}`)}
            >
              ← Back to Batch
            </button>
            <h1>Manage Batch: {batch.name}</h1>
            <p className="batch-subject">{batch.subject}</p>
          </div>
          <div className="header-actions">
            <button 
              className="btn-primary"
              onClick={() => navigate(`/update-batch/${batchId}`)}
            >
              ✏️ Edit Batch
            </button>
            <button 
              className="btn-secondary"
              onClick={() => navigate(`/premium-tests/${batchId}`)}
            >
              📝 View Quizzes
            </button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="stats-grid">
          <div className="stat-card stat-total">
            <div className="stat-icon">👥</div>
            <div className="stat-info">
              <h3>{stats.total}</h3>
              <p>Total Enrolled</p>
            </div>
          </div>
          <div className="stat-card stat-active">
            <div className="stat-icon">✅</div>
            <div className="stat-info">
              <h3>{stats.active}</h3>
              <p>Active Users</p>
            </div>
          </div>
          <div className="stat-card stat-expired">
            <div className="stat-icon">⏰</div>
            <div className="stat-info">
              <h3>{stats.expired}</h3>
              <p>Expired</p>
            </div>
          </div>
          <div className="stat-card stat-suspended">
            <div className="stat-icon">🚫</div>
            <div className="stat-info">
              <h3>{stats.suspended}</h3>
              <p>Suspended</p>
            </div>
          </div>
        </div>

        {/* Enroll User Form */}
        <div className="enroll-section">
          <h2>Enroll New User</h2>
          <form onSubmit={handleEnrollUser} className="enroll-form">
            <div className="form-group">
              <input
                type="email"
                className="form-control"
                placeholder="Enter user email address"
                value={enrollEmail}
                onChange={(e) => setEnrollEmail(e.target.value)}
                disabled={enrolling}
              />
              <small className="form-hint">
                ℹ️ User must have logged in at least once
              </small>
            </div>
            <button 
              type="submit" 
              className="btn-enroll"
              disabled={enrolling || !enrollEmail.trim()}
            >
              {enrolling ? (
                <>
                  <span className="spinner-small"></span> Enrolling...
                </>
              ) : (
                '➕ Enroll User'
              )}
            </button>
          </form>
        </div>

        {/* Enrolled Users Section */}
        <div className="users-section">
          <div className="section-header">
            <h2>Enrolled Users ({filteredUsers.length})</h2>
            <div className="section-controls">
              <div className="search-box">
                <input
                  type="text"
                  placeholder="🔍 Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>
              <select 
                className="filter-select"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="expired">Expired</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
          </div>

          {loadingUsers ? (
            <div className="loading-users">
              <div className="loader"></div>
              <p>Loading enrolled users...</p>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📭</div>
              <h3>No users found</h3>
              <p>
                {searchTerm || filterStatus !== 'all' 
                  ? 'Try adjusting your search or filters'
                  : 'No users enrolled in this batch yet'
                }
              </p>
            </div>
          ) : (
            <div className="users-table-container">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>Profile</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Enrolled Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((enrollment) => (
                    <tr key={enrollment._id}>
                      <td>
                        <img 
                          src={enrollment.userId?.picture || 'https://via.placeholder.com/40'} 
                          alt={enrollment.userId?.name || 'User'}
                          className="user-avatar"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/40';
                          }}
                        />
                      </td>
                      <td>
                        <strong>{enrollment.userId?.name || 'N/A'}</strong>
                      </td>
                      <td>{enrollment.email}</td>
                      <td>
                        {new Date(enrollment.enrolledAt).toLocaleDateString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                      <td>
                        <span className={`status-badge status-${enrollment.status}`}>
                          {enrollment.status}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn-remove"
                          onClick={() => handleRemoveUser(enrollment.userId?._id, enrollment.email)}
                          disabled={removing === enrollment.userId?._id}
                        >
                          {removing === enrollment.userId?._id ? (
                            <>
                              <span className="spinner-small"></span> Removing...
                            </>
                          ) : (
                            '🗑️ Remove'
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageBatch;